/** High-level user actions shared by the UI and keyboard shortcuts. */
import { type Lang, setLang, storeLang, t } from '../i18n/i18n';
import { createDesign, createObject, createThread, newId } from '../engine/model/design';
import { objectBBox, applyTransform } from '../engine/model/geometry';
import { type BBox, bboxUnion, emptyBBox } from '../engine/geometry/polygon';
import type { Design, EmbObject, Shape, StitchParams, StitchType } from '../engine/model/types';
import { decodeStoredImage, type DecodedImage } from '../imaging/decode';
import {
  buildProjectFile,
  fromPortableFile,
  loadProject,
  saveProject,
  toPortableFile,
  type ImageData8,
  type LoadedProject,
} from '../project/db';
import { safeFileName } from '../formats';
import { PROJECT_EXTENSION, parseProject, serializeProject } from '../project/format';
import {
  addObjects,
  cloneObjects,
  deleteObjects,
  duplicateObjects,
  shiftObjects,
  translateObjects,
  updateObjects,
} from '../state/designOps';
import { saveToCloud } from '../account/actions';
import { withPreferredMachine } from '../state/machinePref';
import { type Store, store as defaultStore } from '../state/store';
import { renderThumbnail } from './canvas/render';
import { minNodes, nodePaths, setNodePath } from './canvas/shapes';

export function selectedObjects(s: Store = defaultStore): EmbObject[] {
  const ids = new Set(s.getState().selection);
  return s.design.objects.filter((o) => ids.has(o.id));
}

export function select(ids: string[], s: Store = defaultStore): void {
  s.set({ selection: ids, selectedNode: null });
}

export function toggleSelect(id: string, additive: boolean, s: Store = defaultStore): void {
  const cur = s.getState().selection;
  if (!additive) return select([id], s);
  select(cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id], s);
}

export function selectAll(s: Store = defaultStore): void {
  select(s.design.objects.filter((o) => !o.locked).map((o) => o.id), s);
}

export function deleteSelected(s: Store = defaultStore): void {
  const ids = selectedObjects(s).filter((o) => !o.locked).map((o) => o.id);
  if (!ids.length) return;
  s.commitDesign(deleteObjects(s.design, ids));
  s.set({ selection: [] });
}

export function duplicateSelected(s: Store = defaultStore): void {
  const ids = s.getState().selection;
  if (!ids.length) return;
  const { design, newIds } = duplicateObjects(s.design, ids);
  s.commitDesign(design);
  select(newIds, s);
}

export function copySelected(s: Store = defaultStore): void {
  const objs = selectedObjects(s);
  if (objs.length) s.set({ clipboard: structuredClone(objs) });
}

export function cutSelected(s: Store = defaultStore): void {
  copySelected(s);
  deleteSelected(s);
}

export function paste(s: Store = defaultStore): void {
  const clip = s.getState().clipboard;
  if (!clip.length) return;
  // colours of pasted objects must exist in this design's palette
  let d = s.design;
  const palette = new Map(d.palette.map((c) => [c.id, c]));
  const copies = cloneObjects(clip, { x: 5, y: 5 }).map((o) => {
    if (palette.has(o.colorId)) return o;
    const fallback = d.palette[0] ?? createThread('#1a1a1a', t('Black'));
    if (!palette.has(fallback.id)) {
      d = { ...d, palette: [...d.palette, fallback] };
      palette.set(fallback.id, fallback);
    }
    return { ...o, colorId: fallback.id };
  });
  s.commitDesign(addObjects(d, copies));
  select(copies.map((c) => c.id), s);
  s.set({ clipboard: cloneObjects(clip, { x: 5, y: 5 }) });
}

/** Deletes the selected node in the node tool. Returns false when no node is selected. */
export function deleteSelectedNode(s: Store = defaultStore): boolean {
  const st = s.getState();
  const n = st.selectedNode;
  if (!n || st.tool !== 'node') return false;
  const obj = s.design.objects.find((o) => o.id === n.objectId);
  if (!obj || obj.locked) return true;
  const ref = nodePaths(obj.shape).find((r) => r.key === n.path);
  if (!ref) return true;
  if (ref.nodes.length <= minNodes(ref.closed)) {
    s.toast('info', t('The shape must have at least {n} points.', { n: minNodes(ref.closed) }));
    return true;
  }
  const nodes = ref.nodes.filter((_, i) => i !== n.index);
  s.commitDesign(updateObjects(s.design, [obj.id], (o) => ({ ...o, shape: setNodePath(o.shape, n.path, nodes) })));
  s.set({ selectedNode: null });
  return true;
}

export function nudge(dx: number, dy: number, s: Store = defaultStore): void {
  const ids = s.getState().selection;
  if (ids.length) s.commitDesign(translateObjects(s.design, ids, dx, dy), 'nudge');
}

export function shiftOrder(dir: -1 | 1, s: Store = defaultStore): void {
  const ids = s.getState().selection;
  if (ids.length) s.commitDesign(shiftObjects(s.design, ids, dir));
}

export function patchSelected(f: (o: EmbObject) => EmbObject, key: string | null = null, s: Store = defaultStore): void {
  s.commitDesign(updateObjects(s.design, s.getState().selection, f), key);
}

export function setParams(id: string, params: StitchParams, key: string, s: Store = defaultStore): void {
  s.commitDesign(updateObjects(s.design, [id], (o) => ({ ...o, params })), key);
}

/** Colour used for new objects: first selected object's colour, else first palette colour. */
export function activeColorId(s: Store = defaultStore): { design: Design; colorId: string } {
  const sel = selectedObjects(s)[0];
  if (sel) return { design: s.design, colorId: sel.colorId };
  if (s.design.palette.length) return { design: s.design, colorId: s.design.palette[0].id };
  const th = createThread('#1a1a1a', t('Black'));
  return { design: { ...s.design, palette: [th] }, colorId: th.id };
}

export function addShape(shape: Shape, type: StitchType | StitchParams, s: Store = defaultStore): EmbObject {
  const { design, colorId } = activeColorId(s);
  const obj = createObject(shape, type, colorId);
  s.commitDesign(addObjects(design, [obj]));
  select([obj.id], s);
  return obj;
}

export function selectionBBox(objs: EmbObject[]): BBox {
  return objs.reduce((b, o) => bboxUnion(b, objectBBox(o)), emptyBBox());
}

export function designBBox(d: Design): BBox {
  return selectionBBox(d.objects);
}

/** Fits the given box (mm) into the stage. */
export function zoomToBox(b: BBox, stageW: number, stageH: number, s: Store = defaultStore, margin = 40): void {
  if (!(b.maxX > b.minX) || !(b.maxY > b.minY) || stageW <= 0) return;
  const zoom = Math.max(0.2, Math.min(80, Math.min((stageW - 2 * margin) / (b.maxX - b.minX), (stageH - 2 * margin) / (b.maxY - b.minY))));
  s.set({
    viewport: {
      zoom,
      panX: stageW / 2 - ((b.minX + b.maxX) / 2) * zoom,
      panY: stageH / 2 - ((b.minY + b.maxY) / 2) * zoom,
    },
  });
}

export function zoomToFit(stageW: number, stageH: number, s: Store = defaultStore): void {
  const d = s.design;
  let b = designBBox(d);
  if (!(b.maxX > b.minX)) b = { minX: 0, minY: 0, maxX: d.widthMm || 100, maxY: d.heightMm || 100 };
  if (d.backdrop && s.getState().layers.image) {
    b = bboxUnion(b, { minX: d.backdrop.x, minY: d.backdrop.y, maxX: d.backdrop.x + d.backdrop.widthMm, maxY: d.backdrop.y + d.backdrop.heightMm });
  }
  zoomToBox(b, stageW, stageH, s);
}

// ---------------------------------------------------------------- images

export function setImage(decoded: DecodedImage | null, s: Store = defaultStore): void {
  const prev = s.getState().image;
  if (prev) URL.revokeObjectURL(prev.url);
  if (!decoded) {
    s.set({ image: null });
    return;
  }
  const url = URL.createObjectURL(new Blob([decoded.bytes as BlobPart], { type: decoded.mime }));
  s.set({ image: { decoded, url } });
}

export function imageData8(s: Store): ImageData8 | null {
  const img = s.getState().image;
  if (!img) return null;
  const b = img.decoded.bytes;
  return { bytes: b.buffer.slice(b.byteOffset, b.byteOffset + b.byteLength) as ArrayBuffer, mime: img.decoded.mime, name: img.decoded.name, width: img.decoded.width, height: img.decoded.height };
}

// ---------------------------------------------------------------- projects

export function newProject(s: Store = defaultStore): void {
  setImage(null, s);
  s.resetDesign(withPreferredMachine(createDesign()), { id: null, createdAt: null, lastSaved: null });
  s.set((st) => ({ mode: 'edit', panel: null, pick: null, startCardClosed: false, fitNonce: st.fitNonce + 1 }));
}

/** Closes the current project and starts a new one; asks first when there are unsaved changes. */
export function startNewProject(s: Store = defaultStore): void {
  if (s.getState().project.dirty) s.set({ panel: 'new' });
  else newProject(s);
}

export async function saveCurrentProject(s: Store = defaultStore): Promise<void> {
  const st = s.getState();
  const designAtSave = s.design;
  const id = st.project.id ?? newId('p');
  const name = st.project.name.trim() || t('Untitled');
  const file = buildProjectFile(id, name, s.design, st.project.createdAt ?? undefined, st.project.folder);
  const result = st.stitch.result;
  const thumb = result ? renderThumbnail(result.sequence, 240, 180) : null;
  await saveProject(file, imageData8(s), thumb);
  s.set((cur) => ({ project: { ...cur.project, id, name, createdAt: file.createdAt, dirty: cur.history.present !== designAtSave, lastSaved: file.updatedAt } }));
  // logged-in users save to the cloud; the copy in the browser also works offline
  if (st.account.user) {
    try {
      await saveToCloud(s, false);
      s.toast('success', t('“{name}” is saved in the browser and in the cloud.', { name }));
    } catch (e) {
      s.toast('error', t('“{name}” is saved in the browser, but not in the cloud: {error}', { name, error: e instanceof Error ? e.message : String(e) }));
    }
    return;
  }
  s.toast('success', t('“{name}” is saved locally in the browser.', { name }));
}

export async function openProject(id: string, s: Store = defaultStore): Promise<void> {
  const p = await loadProject(id);
  await applyLoaded(p.file, p.image, s, true);
}

export async function applyLoaded(file: ReturnType<typeof parseProject>, image: ImageData8 | null, s: Store, persisted: boolean): Promise<void> {
  let decoded: DecodedImage | null = null;
  if (image) {
    try {
      decoded = await decodeStoredImage(image.bytes, image.mime, image.name);
    } catch {
      s.toast('error', t('The original image in the project could not be read. The design was opened without an image.'));
    }
  }
  setImage(decoded, s);
  s.resetDesign(file.design, {
    id: persisted ? file.id : null,
    name: file.name,
    createdAt: persisted ? file.createdAt : null,
    lastSaved: persisted ? file.updatedAt : null,
    dirty: !persisted,
    folder: file.folder ?? null,
  });
  s.set((st) => ({ panel: null, mode: 'edit', fitNonce: st.fitNonce + 1 }));
}

export async function exportProjectFile(s: Store = defaultStore): Promise<void> {
  const st = s.getState();
  const file = buildProjectFile(st.project.id ?? newId('p'), st.project.name || t('Untitled'), s.design, st.project.createdAt ?? undefined, st.project.folder);
  downloadProject({ file, image: imageData8(s) });
}

/** Downloads a project as a project file with the image embedded. */
export function downloadProject(p: LoadedProject): void {
  downloadBlob(new Blob([serializeProject(toPortableFile(p))], { type: 'application/json' }), `${safeFileName(p.file.name)}${PROJECT_EXTENSION}`);
}

export async function importProjectFile(f: File, s: Store = defaultStore): Promise<void> {
  const text = await f.text();
  const parsed = parseProject(text);
  const loaded = fromPortableFile(parsed);
  await applyLoaded(loaded.file, loaded.image, s, false);
  s.toast('info', t('Imported “{name}”. Save it to keep it in the browser.', { name: parsed.name }));
}

export function downloadBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

/** Converts a design point to the local coordinates of an object (for node editing). */
export { applyTransform };

/** Switches the UI language, remembers it, and regenerates stitches so messages are translated. */
export function setLanguage(lang: Lang, s: Store = defaultStore): void {
  setLang(lang);
  storeLang(lang);
  if (typeof document !== 'undefined') document.documentElement.lang = lang;
  // notifications were rendered in the old language; clear them
  s.set({ lang, toasts: [] });
  void s.regenerateNow();
}
